import { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  getSubmissionsConfig,
  saveSubmissionsConfig,
  testSubmissionsConnection,
  triggerSubmissionsSync,
  getSubmissionsSyncStatus,
  getSubmissions,
  getSubmissionsDashboardStats,
  getSubmissionsRecruiterStats,
  getSubmissionsCallCorrelation,
  analyzeSubmission,
  createManualSubmission,
  createManualSubmissionWithFile,
  getSubmissionDetail,
  matchSubmissionsWithCalls,
  getCallAudioUrl,
  getCalls
} from '@/lib/api';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// Icons
import {
  BarChart3,
  Database,
  Settings,
  RefreshCw,
  Search,
  Filter,
  Plus,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  Phone,
  Briefcase,
  User,
  FileText,
  TrendingUp,
  TrendingDown,
  Users,
  Building,
  Calendar,
  Loader2,
  Eye,
  Brain,
  Zap,
  Link as LinkIcon,
  ArrowUpRight,
  Star,
  Target,
  Award,
  Activity,
  Play,
  Pause,
  ExternalLink,
  Volume2,
  ChevronUp,
  ChevronDown,
  FileSearch,
  SplitSquareHorizontal,
  ScrollText,
  Maximize2,
  X,
  PhoneCall,
  Mail,
  MapPin,
  Clock,
  Hash,
  Info
} from 'lucide-react';

// ==================== PREVIEW DIALOG COMPONENT ====================
function PreviewDialog({ open, onClose, submission, previewType }) {
  if (!submission) return null;
  
  const data = submission.data || {};
  const resumeText = data.resume_text || data.resume_content || data.resume || '';
  const jobDescription = data.job_description || data.job_requirements || data.requirements || '';
  const jobTitle = data.job_title || data.position_title || 'Job Position';
  const candidateName = data.applicant_full_name || data.applicant_name || data.candidate_name || 'Candidate';

  // Helper to format text with line breaks
  const formatText = (text) => {
    if (!text) return 'No content available';
    return typeof text === 'object' ? JSON.stringify(text, null, 2) : text;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={previewType === 'compare' ? 'max-w-7xl h-[90vh]' : 'max-w-4xl max-h-[85vh]'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {previewType === 'resume' && (
              <>
                <FileText className="h-5 w-5 text-blue-500" />
                Resume - {candidateName}
              </>
            )}
            {previewType === 'job' && (
              <>
                <Briefcase className="h-5 w-5 text-purple-500" />
                Job Description - {jobTitle}
              </>
            )}
            {previewType === 'compare' && (
              <>
                <SplitSquareHorizontal className="h-5 w-5 text-emerald-500" />
                Compare: Job vs Resume
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {previewType === 'compare' 
              ? `Comparing ${jobTitle} requirements with ${candidateName}'s resume`
              : previewType === 'resume' 
                ? `Review candidate's resume and qualifications`
                : `Review job requirements and description`
            }
          </DialogDescription>
        </DialogHeader>

        {previewType === 'compare' ? (
          // Split screen comparison view
          <div className="flex gap-4 h-[calc(90vh-120px)] overflow-hidden">
            {/* Job Description Side */}
            <div className="flex-1 flex flex-col border rounded-lg overflow-hidden">
              <div className="bg-purple-50 dark:bg-purple-900/20 p-3 border-b flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-purple-600" />
                <span className="font-semibold text-purple-700 dark:text-purple-300">Job Description</span>
                <Badge variant="outline" className="ml-auto text-xs">{jobTitle}</Badge>
              </div>
              <ScrollArea className="flex-1 p-4">
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-foreground bg-transparent p-0 m-0">
                    {formatText(jobDescription)}
                  </pre>
                </div>
              </ScrollArea>
            </div>

            {/* Resume Side */}
            <div className="flex-1 flex flex-col border rounded-lg overflow-hidden">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 border-b flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />
                <span className="font-semibold text-blue-700 dark:text-blue-300">Resume</span>
                <Badge variant="outline" className="ml-auto text-xs">{candidateName}</Badge>
              </div>
              <ScrollArea className="flex-1 p-4">
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-foreground bg-transparent p-0 m-0">
                    {formatText(resumeText)}
                  </pre>
                </div>
              </ScrollArea>
            </div>
          </div>
        ) : (
          // Single view (Resume or Job)
          <ScrollArea className="max-h-[calc(85vh-150px)]">
            <div className="p-4">
              {previewType === 'resume' && (
                <div className="space-y-4">
                  {/* Candidate Info Header */}
                  <div className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="h-12 w-12 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-blue-600 dark:text-blue-300" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">{candidateName}</p>
                      <p className="text-muted-foreground text-sm">{data.email || ''}</p>
                      {data.phone && <p className="text-muted-foreground text-sm">{data.phone}</p>}
                    </div>
                  </div>
                  {/* Resume Content */}
                  <div className="border rounded-lg p-4">
                    <pre className="whitespace-pre-wrap font-sans text-sm text-foreground">
                      {formatText(resumeText)}
                    </pre>
                  </div>
                </div>
              )}
              {previewType === 'job' && (
                <div className="space-y-4">
                  {/* Job Info Header */}
                  <div className="flex items-center gap-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="h-12 w-12 bg-purple-200 dark:bg-purple-800 rounded-full flex items-center justify-center">
                      <Briefcase className="h-6 w-6 text-purple-600 dark:text-purple-300" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">{jobTitle}</p>
                      <p className="text-muted-foreground text-sm">{data.client || data.company || ''}</p>
                      {data.location && <p className="text-muted-foreground text-sm">{data.location}</p>}
                    </div>
                  </div>
                  {/* Job Description Content */}
                  <div className="border rounded-lg p-4">
                    <pre className="whitespace-pre-wrap font-sans text-sm text-foreground">
                      {formatText(jobDescription)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SubmissionsPage() {
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const tenantId = tenant?.id || user?.tenant_id;
  const isAdmin = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'quality_lead';

  // Tab state
  const [activeTab, setActiveTab] = useState('dashboard');

  // Config state
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  // Load configuration on mount
  useEffect(() => {
    if (tenantId) {
      loadConfig();
    }
  }, [tenantId]);

  const loadConfig = async () => {
    try {
      setConfigLoading(true);
      const { data } = await getSubmissionsConfig(tenantId);
      setConfig(data);
    } catch (error) {
      console.error('Failed to load submissions config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="submissions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Database className="h-7 w-7 text-blue-500" />
            Submissions
          </h1>
          <p className="text-muted-foreground mt-1">
            AI-powered candidate submission analysis with call correlation
          </p>
        </div>
        
        {config?.configured && (
          <div className="flex items-center gap-2">
            <Badge variant={config.config?.connection_status === 'connected' ? 'success' : 'destructive'}>
              {config.config?.connection_status === 'connected' ? (
                <><CheckCircle className="h-3 w-3 mr-1" /> Connected</>
              ) : (
                <><XCircle className="h-3 w-3 mr-1" /> Disconnected</>
              )}
            </Badge>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted border border-border">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="data" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Database className="h-4 w-4 mr-2" />
            Submissions Data
          </TabsTrigger>
          <TabsTrigger value="config" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Settings className="h-4 w-4 mr-2" />
            API Configuration
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <DashboardTab tenantId={tenantId} isAdmin={isAdmin} userId={user?.id} config={config} />
        </TabsContent>

        {/* Submissions Data Tab */}
        <TabsContent value="data">
          <SubmissionsDataTab tenantId={tenantId} isAdmin={isAdmin} userId={user?.id} config={config} />
        </TabsContent>

        {/* API Configuration Tab */}
        <TabsContent value="config">
          <APIConfigTab tenantId={tenantId} config={config} onConfigSaved={loadConfig} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ==================== DRILL-DOWN DIALOG COMPONENT ====================
function DrillDownDialog({ open, onClose, title, description, icon, data, type, tenantId }) {
  const [loading, setLoading] = useState(false);
  const [detailData, setDetailData] = useState([]);
  
  useEffect(() => {
    if (open && tenantId) {
      loadDetailData();
    }
  }, [open, type, tenantId]);

  const loadDetailData = async () => {
    setLoading(true);
    try {
      let params = { limit: 50 };
      
      // Add type-specific filters
      if (type === 'total') {
        // All submissions
      } else if (type === 'with_calls') {
        params.has_calls = 'yes';
      } else if (type === 'without_calls') {
        params.has_calls = 'no';
      } else if (type === 'pending') {
        params.analyzed = false;
      } else if (type === 'high_score') {
        params.min_fit_score = 70;
      } else if (type === 'medium_score') {
        params.min_fit_score = 50;
        params.max_fit_score = 69;
      } else if (type === 'low_score') {
        params.max_fit_score = 49;
      } else if (type === 'recommend') {
        params.recommendation = 'Recommend';
      } else if (type === 'consider') {
        params.recommendation = 'Consider';
      } else if (type === 'not_recommend') {
        params.recommendation = 'Not Recommend';
      } else if (type === 'recruiter') {
        params.recruiter = data?.recruiter_name;
      } else if (type === 'client') {
        params.client = data?.client_name;
      }
      
      const res = await getSubmissions(tenantId, params);
      setDetailData(res.data.submissions || []);
    } catch (error) {
      console.error('Failed to load detail data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get candidate name
  const getCandidateName = (sub) => {
    const d = sub.data || {};
    return d.applicant_full_name || d.applicant_name || d.candidate_name || 'Unknown';
  };

  // Helper to get score badge color
  const getScoreBadge = (score) => {
    if (!score) return <Badge variant="outline">N/A</Badge>;
    if (score >= 70) return <Badge className="bg-emerald-100 text-emerald-700">{score}</Badge>;
    if (score >= 50) return <Badge className="bg-amber-100 text-amber-700">{score}</Badge>;
    return <Badge className="bg-red-100 text-red-700">{score}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            {icon}
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : detailData.length === 0 ? (
            <div className="text-center py-12">
              <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No submissions found matching this criteria</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-center">Fit Score</TableHead>
                  <TableHead className="text-center">Calls</TableHead>
                  <TableHead>Recommendation</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailData.map((sub) => (
                  <TableRow key={sub.submission_id} className="hover:bg-muted/50">
                    <TableCell>
                      <div>
                        <p className="font-medium">{getCandidateName(sub)}</p>
                        <p className="text-xs text-muted-foreground">{sub.data?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="truncate max-w-[150px]">{sub.data?.job_title || 'N/A'}</p>
                    </TableCell>
                    <TableCell>
                      <p className="truncate max-w-[120px]">{sub.data?.client || 'N/A'}</p>
                    </TableCell>
                    <TableCell className="text-center">
                      {getScoreBadge(sub.analysis?.overall_fit_score)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={sub.matched_calls_count > 0 ? 'default' : 'outline'}>
                        {sub.matched_calls_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {sub.analysis?.overall_recommendation ? (
                        <Badge className={
                          sub.analysis.overall_recommendation === 'Recommend' ? 'bg-emerald-100 text-emerald-700' :
                          sub.analysis.overall_recommendation === 'Consider' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {sub.analysis.overall_recommendation}
                        </Badge>
                      ) : (
                        <Badge variant="outline">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ScrollArea>

        <DialogFooter className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {detailData.length} submissions
          </p>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== CLICKABLE METRIC CARD ====================
function ClickableMetricCard({ title, value, subtitle, suffix, icon, color, onClick, description }) {
  const colorMap = {
    blue: 'from-blue-500/10 to-blue-600/5 dark:from-blue-500/20 dark:to-blue-600/10 border-blue-400/50 dark:border-blue-500/30 hover:border-blue-500',
    green: 'from-emerald-500/10 to-emerald-600/5 dark:from-emerald-500/20 dark:to-emerald-600/10 border-emerald-400/50 dark:border-emerald-500/30 hover:border-emerald-500',
    purple: 'from-purple-500/10 to-purple-600/5 dark:from-purple-500/20 dark:to-purple-600/10 border-purple-400/50 dark:border-purple-500/30 hover:border-purple-500',
    orange: 'from-orange-500/10 to-orange-600/5 dark:from-orange-500/20 dark:to-orange-600/10 border-orange-400/50 dark:border-orange-500/30 hover:border-orange-500',
    red: 'from-red-500/10 to-red-600/5 dark:from-red-500/20 dark:to-red-600/10 border-red-400/50 dark:border-red-500/30 hover:border-red-500',
    yellow: 'from-amber-500/10 to-amber-600/5 dark:from-amber-500/20 dark:to-amber-600/10 border-amber-400/50 dark:border-amber-500/30 hover:border-amber-500'
  };

  const iconColorMap = {
    blue: 'text-blue-600 dark:text-blue-400 bg-blue-500/20 dark:bg-blue-500/30',
    green: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/20 dark:bg-emerald-500/30',
    purple: 'text-purple-600 dark:text-purple-400 bg-purple-500/20 dark:bg-purple-500/30',
    orange: 'text-orange-600 dark:text-orange-400 bg-orange-500/20 dark:bg-orange-500/30',
    red: 'text-red-600 dark:text-red-400 bg-red-500/20 dark:bg-red-500/30',
    yellow: 'text-amber-600 dark:text-amber-400 bg-amber-500/20 dark:bg-amber-500/30'
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Card 
            className={`bg-gradient-to-br ${colorMap[color] || colorMap.blue} cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]`}
            onClick={onClick}
          >
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{title}</p>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-3xl font-bold text-foreground">{value}</span>
                    {suffix && <span className="text-lg text-muted-foreground">{suffix}</span>}
                  </div>
                  {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
                </div>
                <div className={`p-3 rounded-lg ${iconColorMap[color] || iconColorMap.blue}`}>
                  {icon}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                <Eye className="h-3 w-3" />
                Click to view details
              </div>
            </CardContent>
          </Card>
        </TooltipTrigger>
        <TooltipContent>
          <p>{description || `View ${title.toLowerCase()} details`}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ==================== DASHBOARD TAB ====================
function DashboardTab({ tenantId, isAdmin, userId, config }) {
  const [stats, setStats] = useState(null);
  const [recruiterStats, setRecruiterStats] = useState([]);
  const [correlation, setCorrelation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  
  // Drill-down dialog state
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownType, setDrillDownType] = useState(null);
  const [drillDownTitle, setDrillDownTitle] = useState('');
  const [drillDownDesc, setDrillDownDesc] = useState('');
  const [drillDownIcon, setDrillDownIcon] = useState(null);
  const [drillDownData, setDrillDownData] = useState(null);

  // Open drill-down dialog
  const openDrillDown = (type, title, description, icon, data = null) => {
    setDrillDownType(type);
    setDrillDownTitle(title);
    setDrillDownDesc(description);
    setDrillDownIcon(icon);
    setDrillDownData(data);
    setDrillDownOpen(true);
  };

  useEffect(() => {
    if (tenantId) {
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [tenantId, dateRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));
      
      const params = {
        start_date: startDate.toISOString(),
        is_admin: isAdmin,
        user_id: userId
      };

      const [statsRes, recruitersRes, correlationRes] = await Promise.all([
        getSubmissionsDashboardStats(tenantId, params),
        getSubmissionsRecruiterStats(tenantId, params),
        getSubmissionsCallCorrelation(tenantId, params)
      ]);

      setStats(statsRes.data);
      setRecruiterStats(recruitersRes.data.recruiters || []);
      setCorrelation(correlationRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (!config?.configured && !stats && !loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="flex flex-col items-center justify-center py-16">
          <Settings className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No Submissions Data</h3>
          <p className="text-muted-foreground text-center max-w-md mb-4">
            Configure CEIPAL API or add manual submissions to see analytics here.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Analytics Overview</h2>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-40 bg-background border-input">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="60">Last 60 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 6 months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics - CLICKABLE */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ClickableMetricCard
          title="Total Submissions"
          value={stats?.total_submissions || 0}
          icon={<Database className="h-5 w-5" />}
          color="blue"
          description="View all submissions"
          onClick={() => openDrillDown(
            'total',
            'All Submissions',
            `All ${stats?.total_submissions || 0} submissions in the selected period`,
            <Database className="h-5 w-5 text-blue-500" />
          )}
        />
        <ClickableMetricCard
          title="Avg Fit Score"
          value={stats?.average_fit_score || 0}
          suffix="/100"
          icon={<Target className="h-5 w-5" />}
          color={stats?.average_fit_score >= 70 ? 'green' : stats?.average_fit_score >= 50 ? 'yellow' : 'red'}
          description="View submissions by score range"
          onClick={() => openDrillDown(
            'high_score',
            'High Fit Score Submissions',
            'Submissions with fit score 70 or above',
            <Target className="h-5 w-5 text-emerald-500" />
          )}
        />
        <ClickableMetricCard
          title="With Calls"
          value={stats?.submissions_with_calls || 0}
          subtitle={`${stats?.submissions_without_calls || 0} without calls`}
          icon={<Phone className="h-5 w-5" />}
          color="purple"
          description="View submissions that have matched calls"
          onClick={() => openDrillDown(
            'with_calls',
            'Submissions With Calls',
            `${stats?.submissions_with_calls || 0} submissions that have matched phone calls`,
            <Phone className="h-5 w-5 text-purple-500" />
          )}
        />
        <ClickableMetricCard
          title="Pending Analysis"
          value={stats?.pending_analysis || 0}
          icon={<Brain className="h-5 w-5" />}
          color="orange"
          description="View submissions awaiting AI analysis"
          onClick={() => openDrillDown(
            'pending',
            'Pending Analysis',
            `${stats?.pending_analysis || 0} submissions awaiting AI analysis`,
            <Brain className="h-5 w-5 text-orange-500" />
          )}
        />
      </div>

      {/* Score Breakdown - Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 dark:from-emerald-500/20 dark:to-emerald-600/10 border-emerald-400/50 dark:border-emerald-500/30 cursor-pointer hover:shadow-lg hover:border-emerald-500 transition-all"
          onClick={() => openDrillDown('high_score', 'High Score (≥70)', 'Submissions scoring 70 or above', <TrendingUp className="h-5 w-5 text-emerald-500" />)}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">High Score (≥70)</p>
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{stats?.high_score_count || 0}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 dark:from-amber-500/20 dark:to-amber-600/10 border-amber-400/50 dark:border-amber-500/30 cursor-pointer hover:shadow-lg hover:border-amber-500 transition-all"
          onClick={() => openDrillDown('medium_score', 'Medium Score (50-69)', 'Submissions scoring between 50 and 69', <Target className="h-5 w-5 text-amber-500" />)}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Medium Score (50-69)</p>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{stats?.medium_score_count || 0}</p>
              </div>
              <Target className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className="bg-gradient-to-br from-red-500/10 to-red-600/5 dark:from-red-500/20 dark:to-red-600/10 border-red-400/50 dark:border-red-500/30 cursor-pointer hover:shadow-lg hover:border-red-500 transition-all"
          onClick={() => openDrillDown('low_score', 'Low Score (<50)', 'Submissions scoring below 50', <TrendingDown className="h-5 w-5 text-red-500" />)}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 dark:text-red-400 font-medium">Low Score (&lt;50)</p>
                <p className="text-2xl font-bold text-red-700 dark:text-red-300">{stats?.low_score_count || 0}</p>
              </div>
              <TrendingDown className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Submissions Trend */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              Submissions Trend
            </CardTitle>
            <CardDescription className="text-muted-foreground">Daily submissions over the period</CardDescription>
          </CardHeader>
          <CardContent>
            <TrendChart data={stats?.trend || []} />
          </CardContent>
        </Card>

        {/* Fit Score Distribution */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-500" />
              Fit Score Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScoreDistributionChart data={stats?.score_distribution || []} />
          </CardContent>
        </Card>
      </div>

      {/* Recommendation Breakdown - Clickable */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Target className="h-5 w-5 text-green-500" />
              Recommendation Breakdown
            </CardTitle>
            <CardDescription>Click on each category to see submissions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div 
                className="flex items-center justify-between p-3 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-lg cursor-pointer hover:bg-emerald-500/20 dark:hover:bg-emerald-500/30 transition-colors"
                onClick={() => openDrillDown('recommend', 'Recommended Candidates', 'Candidates recommended for hire', <CheckCircle className="h-5 w-5 text-emerald-500" />)}
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  <span className="font-medium text-emerald-700 dark:text-emerald-300">Recommend</span>
                </div>
                <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">{stats?.recommend_count || 0}</Badge>
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-amber-500/10 dark:bg-amber-500/20 rounded-lg cursor-pointer hover:bg-amber-500/20 dark:hover:bg-amber-500/30 transition-colors"
                onClick={() => openDrillDown('consider', 'Consider Candidates', 'Candidates to consider with caveats', <AlertCircle className="h-5 w-5 text-amber-500" />)}
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  <span className="font-medium text-amber-700 dark:text-amber-300">Consider</span>
                </div>
                <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-300">{stats?.consider_count || 0}</Badge>
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-red-500/10 dark:bg-red-500/20 rounded-lg cursor-pointer hover:bg-red-500/20 dark:hover:bg-red-500/30 transition-colors"
                onClick={() => openDrillDown('not_recommend', 'Not Recommended', 'Candidates not recommended', <XCircle className="h-5 w-5 text-red-500" />)}
              >
                <div className="flex items-center gap-3">
                  <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                  <span className="font-medium text-red-700 dark:text-red-300">Not Recommend</span>
                </div>
                <Badge className="bg-red-500/20 text-red-700 dark:text-red-300">{stats?.not_recommend_count || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Call Quality Correlation */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Activity className="h-5 w-5 text-orange-500" />
              Call Quality vs Fit Score
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {correlation?.insight}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CorrelationChart data={correlation?.correlation || {}} />
          </CardContent>
        </Card>
      </div>

      {/* Top Recruiters - Clickable */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Award className="h-5 w-5 text-yellow-500" />
            Top Recruiters by Submission Quality
          </CardTitle>
          <CardDescription>Click on a recruiter to see their submissions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recruiterStats.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No recruiter data available</p>
            ) : (
              recruiterStats.map((recruiter, idx) => (
                <div 
                  key={recruiter._id || idx} 
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors"
                  onClick={() => openDrillDown(
                    'recruiter',
                    `${recruiter.recruiter_name || 'Unknown'}'s Submissions`,
                    `All submissions by ${recruiter.recruiter_name || 'Unknown'}`,
                    <User className="h-5 w-5 text-blue-500" />,
                    { recruiter_name: recruiter.recruiter_name }
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      idx === 0 ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400' :
                      idx === 1 ? 'bg-slate-400/20 text-muted-foreground' :
                      idx === 2 ? 'bg-orange-500/20 text-orange-600 dark:text-orange-400' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {idx === 0 ? <Star className="h-4 w-4" /> : idx + 1}
                    </div>
                    <div>
                      <p className="text-foreground font-medium">{recruiter.recruiter_name || 'Unknown'}</p>
                      <p className="text-muted-foreground text-sm">{recruiter.total_submissions} submissions</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-foreground font-semibold">{Math.round(recruiter.avg_fit_score || 0)}</p>
                      <p className="text-muted-foreground text-xs">Avg Score</p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-semibold">{Math.round(recruiter.quality_rate || 0)}%</p>
                      <p className="text-muted-foreground text-xs">Quality Rate</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Submissions by Client - Clickable */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Building className="h-5 w-5 text-purple-400" />
            Submissions by Client
          </CardTitle>
          <CardDescription>Click on a client to see their submissions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(stats?.by_client || []).length === 0 ? (
              <p className="text-muted-foreground text-center py-8 col-span-full">No client data available</p>
            ) : (
              (stats?.by_client || []).map((client, idx) => (
                <div 
                  key={client._id || idx} 
                  className="p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors group"
                  onClick={() => openDrillDown(
                    'client',
                    `${client._id || 'Unknown'} Submissions`,
                    `All submissions for ${client._id || 'Unknown'}`,
                    <Building className="h-5 w-5 text-purple-500" />,
                    { client_name: client._id }
                  )}
                >
                  <div className="flex items-start justify-between">
                    <p className="text-foreground font-medium truncate flex-1">{client._id || 'Unknown Client'}</p>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-muted-foreground">{client.count} submissions</span>
                    <Badge variant={client.avg_fit_score >= 70 ? 'success' : client.avg_fit_score >= 50 ? 'warning' : 'destructive'}>
                      {Math.round(client.avg_fit_score || 0)} avg
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Drill-Down Dialog */}
      <DrillDownDialog
        open={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        title={drillDownTitle}
        description={drillDownDesc}
        icon={drillDownIcon}
        data={drillDownData}
        type={drillDownType}
        tenantId={tenantId}
      />
    </div>
  );
}

// ==================== SUBMISSIONS DATA TAB ====================
function SubmissionsDataTab({ tenantId, isAdmin, userId, config }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    dateRange: '30',
    minFitScore: '',
    maxFitScore: '',
    hasCalls: ''
  });
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAddManual, setShowAddManual] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [analyzingIds, setAnalyzingIds] = useState(new Set());
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Preview dialog state
  const [previewSubmission, setPreviewSubmission] = useState(null);
  const [previewType, setPreviewType] = useState(null); // 'resume', 'job', or 'compare'
  
  // Detail popup states
  const [candidateDetailPopup, setCandidateDetailPopup] = useState(null);
  const [jobDetailPopup, setJobDetailPopup] = useState(null);
  const [callsListPopup, setCallsListPopup] = useState(null);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [callsList, setCallsList] = useState([]);

  // Open preview dialog
  const openPreview = (sub, type) => {
    setPreviewSubmission(sub);
    setPreviewType(type);
  };

  // Close preview dialog
  const closePreview = () => {
    setPreviewSubmission(null);
    setPreviewType(null);
  };
  
  // Load calls for a phone number
  const loadCallsForPhone = async (sub) => {
    const phone = sub.data?.phone || sub.data?.mobile;
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    
    setCallsListPopup(sub);
    setLoadingCalls(true);
    try {
      const response = await getCalls({ phone_number: phone, limit: 50 });
      setCallsList(response.data.calls || []);
    } catch (error) {
      console.error('Error loading calls:', error);
      toast.error('Failed to load calls');
      setCallsList([]);
    } finally {
      setLoadingCalls(false);
    }
  };

  // Helper to get candidate/applicant name from CEIPAL data
  const getCandidateName = (sub) => {
    const data = sub.data || {};
    return data.applicant_full_name || data.applicant_name || data.candidate_name || data.full_name || data.name || 'Unknown';
  };
  
  // Helper to get phone number
  const getPhone = (sub) => {
    return sub.data?.phone || sub.data?.mobile || sub.data?.cell_phone || '';
  };

  // Helper to get job ID
  const getJobId = (sub) => {
    return sub.job_id || sub.data?.job_code || sub.data?.job_id || 'N/A';
  };

  // Helper to get job title
  const getJobTitle = (sub) => {
    return sub.data?.job_title || sub.data?.position_title || 'N/A';
  };

  // Sorting handler
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted submissions
  const sortedSubmissions = useMemo(() => {
    if (!sortConfig.key) return submissions;
    
    return [...submissions].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'candidate':
          aVal = getCandidateName(a).toLowerCase();
          bVal = getCandidateName(b).toLowerCase();
          break;
        case 'job_id':
          aVal = getJobId(a).toLowerCase();
          bVal = getJobId(b).toLowerCase();
          break;
        case 'job_title':
          aVal = getJobTitle(a).toLowerCase();
          bVal = getJobTitle(b).toLowerCase();
          break;
        case 'phone':
          aVal = (a.data?.phone || a.data?.mobile || '').toLowerCase();
          bVal = (b.data?.phone || b.data?.mobile || '').toLowerCase();
          break;
        case 'fit_score':
          aVal = a.analysis?.overall_fit_score || 0;
          bVal = b.analysis?.overall_fit_score || 0;
          break;
        case 'calls':
          aVal = a.matched_calls_count || 0;
          bVal = b.matched_calls_count || 0;
          break;
        case 'status':
          aVal = a.analyzed ? 1 : 0;
          bVal = b.analyzed ? 1 : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [submissions, sortConfig]);

  // Sortable header component
  const SortableHeader = ({ label, sortKey }) => (
    <th 
      className="text-left text-muted-foreground text-sm font-medium p-4 cursor-pointer hover:bg-muted/50 select-none"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === sortKey && (
          sortConfig.direction === 'asc' 
            ? <ChevronUp className="h-4 w-4" />
            : <ChevronDown className="h-4 w-4" />
        )}
      </div>
    </th>
  );

  useEffect(() => {
    if (tenantId) {
      // Always load submissions - both CEIPAL and manual submissions
      loadSubmissions();
    } else {
      setLoading(false);
    }
  }, [tenantId, page, search, filters]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(filters.dateRange));
      
      const params = {
        page,
        limit: 20,
        search: search || undefined,
        start_date: startDate.toISOString(),
        is_admin: isAdmin,
        user_id: userId,
        min_fit_score: filters.minFitScore || undefined,
        max_fit_score: filters.maxFitScore || undefined,
        has_calls: filters.hasCalls === 'yes' ? true : filters.hasCalls === 'no' ? false : undefined
      };

      const { data } = await getSubmissions(tenantId, params);
      setSubmissions(data.submissions || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await triggerSubmissionsSync(tenantId);
      toast.success('Sync started! This may take a few minutes.');
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        const { data } = await getSubmissionsSyncStatus(tenantId);
        if (data.current?.status !== 'running') {
          clearInterval(pollInterval);
          setSyncing(false);
          loadSubmissions();
          if (data.current?.status === 'completed') {
            toast.success(`Sync completed: ${data.current.submissions_synced} submissions synced`);
          } else if (data.current?.status === 'completed_with_errors') {
            toast.warning(`Sync completed with errors: ${data.current.submissions_synced} submissions synced`);
          }
        }
      }, 3000);
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Failed to start sync');
      setSyncing(false);
    }
  };

  const viewSubmissionDetail = async (submission) => {
    try {
      setDetailLoading(true);
      const { data } = await getSubmissionDetail(tenantId, submission.submission_id);
      setSelectedSubmission(data);
    } catch (error) {
      console.error('Failed to load submission detail:', error);
      toast.error('Failed to load submission details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAnalyze = async (submissionId) => {
    setAnalyzingIds(prev => new Set([...prev, submissionId]));
    try {
      toast.info('AI analysis started...');
      const { data } = await analyzeSubmission(tenantId, submissionId);
      toast.success('Analysis completed!');
      loadSubmissions();
      if (selectedSubmission?.submission?.submission_id === submissionId) {
        viewSubmissionDetail(selectedSubmission.submission);
      }
    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error('Analysis failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
    }
  };

  const handleBulkAnalyze = async () => {
    const pendingSubmissions = submissions.filter(s => !s.analyzed);
    if (pendingSubmissions.length === 0) {
      toast.info('No pending submissions to analyze');
      return;
    }

    setBulkAnalyzing(true);
    let successCount = 0;
    let failCount = 0;

    toast.info(`Starting bulk analysis of ${pendingSubmissions.length} submissions...`);

    for (const sub of pendingSubmissions) {
      try {
        setAnalyzingIds(prev => new Set([...prev, sub.submission_id]));
        await analyzeSubmission(tenantId, sub.submission_id);
        successCount++;
      } catch (error) {
        console.error(`Analysis failed for ${sub.submission_id}:`, error);
        failCount++;
      } finally {
        setAnalyzingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(sub.submission_id);
          return newSet;
        });
      }
    }

    setBulkAnalyzing(false);
    loadSubmissions();
    
    if (failCount === 0) {
      toast.success(`Bulk analysis completed! ${successCount} submissions analyzed.`);
    } else {
      toast.warning(`Bulk analysis completed: ${successCount} succeeded, ${failCount} failed.`);
    }
  };

  const pendingCount = submissions.filter(s => !s.analyzed).length;
  const [refreshingCalls, setRefreshingCalls] = useState(false);

  const handleRefreshCallMatches = async () => {
    try {
      setRefreshingCalls(true);
      toast.info('Syncing submissions with calls...');
      const { data } = await matchSubmissionsWithCalls(tenantId);
      toast.success(`Matched ${data.submissions_matched} of ${data.submissions_processed} submissions with calls`);
      loadSubmissions();
    } catch (error) {
      console.error('Failed to refresh call matches:', error);
      toast.error('Failed to sync with calls');
    } finally {
      setRefreshingCalls(false);
    }
  };

  // Show data table if there are submissions OR if CEIPAL is configured
  // Only show "not configured" message if no submissions AND CEIPAL not configured
  if (!config?.configured && submissions.length === 0 && !loading) {
    return (
      <div className="space-y-4">
        {/* Actions Bar - Show Add Manual even without CEIPAL config */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Submissions Data</h3>
            <p className="text-muted-foreground text-sm">Add manual submissions or configure CEIPAL to sync automatically</p>
          </div>
          <Button onClick={() => setShowAddManual(true)} data-testid="add-manual-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Manual Submission
          </Button>
        </div>

        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Settings className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Submissions Yet</h3>
            <p className="text-muted-foreground text-center max-w-md mb-4">
              Configure CEIPAL API in the API Configuration tab to automatically sync submissions, 
              or use the &quot;Add Manual Submission&quot; button above to enter submissions manually.
            </p>
          </CardContent>
        </Card>

        {/* Add Manual Submission Dialog */}
        <AddManualSubmissionDialog
          open={showAddManual}
          onClose={() => setShowAddManual(false)}
          tenantId={tenantId}
          onSuccess={() => {
            setShowAddManual(false);
            loadSubmissions();
            toast.success('Manual submission created!');
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Actions Bar */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[300px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by phone, name, or job ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-background border-input"
              data-testid="submissions-search"
            />
          </div>
        </div>
        
        <Select value={filters.dateRange} onValueChange={(v) => setFilters({...filters, dateRange: v})}>
          <SelectTrigger className="w-36 bg-background border-input">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="60">Last 60 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 6 months</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.hasCalls} onValueChange={(v) => setFilters({...filters, hasCalls: v})}>
          <SelectTrigger className="w-36 bg-background border-input">
            <Phone className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Calls" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="yes">With Calls</SelectItem>
            <SelectItem value="no">No Calls</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          onClick={handleSync}
          disabled={syncing}
          className="border-border"
          data-testid="sync-btn"
        >
          {syncing ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Syncing...</>
          ) : (
            <><RefreshCw className="h-4 w-4 mr-2" /> Sync from CEIPAL</>
          )}
        </Button>

        <Button
          variant="outline"
          onClick={handleRefreshCallMatches}
          disabled={refreshingCalls}
          className="border-green-500/50 text-green-400 hover:bg-green-500/10"
          data-testid="refresh-calls-btn"
        >
          {refreshingCalls ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Refreshing...</>
          ) : (
            <><Link className="h-4 w-4 mr-2" /> Refresh Calls</>
          )}
        </Button>

        {pendingCount > 0 && (
          <Button
            variant="outline"
            onClick={handleBulkAnalyze}
            disabled={bulkAnalyzing}
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            data-testid="bulk-analyze-btn"
          >
            {bulkAnalyzing ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing...</>
            ) : (
              <><Brain className="h-4 w-4 mr-2" /> Analyze All ({pendingCount})</>
            )}
          </Button>
        )}

        <Button 
          onClick={() => setShowAddManual(true)} 
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-manual-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Submission
        </Button>
      </div>

      {/* Submissions Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : submissions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Database className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No submissions found</p>
              <p className="text-muted-foreground text-sm">Try adjusting your filters or sync from CEIPAL</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <SortableHeader label="Candidate" sortKey="candidate" />
                    <SortableHeader label="Job ID" sortKey="job_id" />
                    <SortableHeader label="Job Title" sortKey="job_title" />
                    <SortableHeader label="Phone" sortKey="phone" />
                    <SortableHeader label="Fit Score" sortKey="fit_score" />
                    <SortableHeader label="Calls" sortKey="calls" />
                    <SortableHeader label="Status" sortKey="status" />
                    <th className="text-left text-muted-foreground text-sm font-medium p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedSubmissions.map((sub) => (
                    <tr key={sub.submission_id} className="hover:bg-muted/30 transition-colors">
                      {/* Candidate - Clickable */}
                      <td className="p-4">
                        <div 
                          className="cursor-pointer hover:text-blue-400 transition-colors"
                          onClick={() => setCandidateDetailPopup(sub)}
                        >
                          <p className="text-foreground font-medium flex items-center gap-1">
                            {getCandidateName(sub)}
                            <ExternalLink className="h-3 w-3 opacity-50" />
                          </p>
                          <p className="text-muted-foreground text-sm">{sub.data?.email}</p>
                        </div>
                      </td>
                      
                      {/* Job ID - Clickable */}
                      <td className="p-4">
                        <button
                          className="text-blue-400 hover:text-blue-300 font-mono text-sm underline decoration-dotted underline-offset-2 cursor-pointer"
                          onClick={() => setJobDetailPopup(sub)}
                        >
                          {getJobId(sub)}
                        </button>
                      </td>
                      
                      {/* Job Title - Clickable to show Job Description */}
                      <td className="p-4">
                        <div>
                          <button
                            className="text-foreground hover:text-purple-400 transition-colors cursor-pointer text-left"
                            onClick={() => openPreview(sub, 'job')}
                          >
                            <span className="flex items-center gap-1">
                              {getJobTitle(sub)}
                              <FileText className="h-3 w-3 opacity-50" />
                            </span>
                          </button>
                          <p className="text-muted-foreground text-sm">{sub.data?.client}</p>
                        </div>
                      </td>
                      
                      {/* Phone - Clickable to show calls */}
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {getPhone(sub) ? (
                            <button
                              className="text-emerald-400 hover:text-emerald-300 font-mono text-sm cursor-pointer flex items-center gap-1"
                              onClick={() => loadCallsForPhone(sub)}
                            >
                              <Phone className="h-3 w-3" />
                              {getPhone(sub)}
                            </button>
                          ) : (
                            <span className="text-muted-foreground/70">N/A</span>
                          )}
                        </div>
                      </td>
                      
                      {/* Fit Score */}
                      <td className="p-4">
                        {sub.analysis?.overall_fit_score ? (
                          <FitScoreBadge score={sub.analysis.overall_fit_score} />
                        ) : analyzingIds.has(sub.submission_id) ? (
                          <Badge variant="outline" className="text-blue-400 border-blue-400/30">
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Analyzing...
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">Not Analyzed</Badge>
                        )}
                      </td>
                      
                      {/* Calls - Clickable */}
                      <td className="p-4">
                        {(sub.matched_calls_count || 0) > 0 ? (
                          <button
                            className="flex items-center gap-1 text-green-400 hover:text-green-300 cursor-pointer"
                            onClick={() => loadCallsForPhone(sub)}
                          >
                            <PhoneCall className="h-4 w-4" />
                            <span className="font-medium">{sub.matched_calls_count}</span>
                            <span className="text-xs text-muted-foreground">calls</span>
                          </button>
                        ) : (
                          <span className="text-muted-foreground/50">0 calls</span>
                        )}
                      </td>
                      
                      {/* Status */}
                      <td className="p-4">
                        {sub.analyzed ? (
                          <Badge variant="success" className="bg-green-500/20 text-green-400">
                            <CheckCircle className="h-3 w-3 mr-1" /> Analyzed
                          </Badge>
                        ) : analyzingIds.has(sub.submission_id) ? (
                          <Badge variant="outline" className="text-blue-400 border-blue-400/30 animate-pulse">
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Processing
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-yellow-400 border-yellow-400/30">
                            <AlertCircle className="h-3 w-3 mr-1" /> Pending
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <TooltipProvider>
                          <div className="flex items-center gap-1">
                            {/* View Details */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => viewSubmissionDetail(sub)}
                                  data-testid={`view-${sub.submission_id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View Details</TooltipContent>
                            </Tooltip>
                            
                            {/* Resume Preview */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openPreview(sub, 'resume')}
                                  className="text-blue-500 hover:text-blue-600 hover:bg-blue-500/10 dark:hover:bg-blue-500/20"
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Preview Resume</TooltipContent>
                            </Tooltip>
                            
                            {/* Job Description Preview */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openPreview(sub, 'job')}
                                  className="text-purple-500 hover:text-purple-600 hover:bg-purple-500/10 dark:hover:bg-purple-500/20"
                                >
                                  <Briefcase className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Preview Job Description</TooltipContent>
                            </Tooltip>
                            
                            {/* Compare Side-by-Side */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openPreview(sub, 'compare')}
                                  className="text-emerald-500 hover:text-emerald-600 hover:bg-emerald-500/10 dark:hover:bg-emerald-500/20"
                                >
                                  <SplitSquareHorizontal className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Compare Job vs Resume (Split View)</TooltipContent>
                            </Tooltip>
                            
                            {/* AI Analyze */}
                            {!sub.analyzed && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleAnalyze(sub.submission_id)}
                                    disabled={analyzingIds.has(sub.submission_id)}
                                    className={analyzingIds.has(sub.submission_id) ? "text-blue-300" : "text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"}
                                    data-testid={`analyze-${sub.submission_id}`}
                                  >
                                    {analyzingIds.has(sub.submission_id) ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Brain className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Run AI Analysis</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TooltipProvider>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {total > 20 && (
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-muted-foreground text-sm">
              Showing {((page - 1) * 20) + 1} - {Math.min(page * 20, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page * 20 >= total}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Submission Detail Dialog */}
      <SubmissionDetailDialog
        open={!!selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
        data={selectedSubmission}
        loading={detailLoading}
        onAnalyze={handleAnalyze}
      />

      {/* Preview Dialog */}
      <PreviewDialog
        open={!!previewSubmission}
        onClose={closePreview}
        submission={previewSubmission}
        previewType={previewType}
      />

      {/* Candidate Detail Popup */}
      <Dialog open={!!candidateDetailPopup} onOpenChange={() => setCandidateDetailPopup(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-500" />
              Candidate Details
            </DialogTitle>
            <DialogDescription>
              Full applicant information from CEIPAL
            </DialogDescription>
          </DialogHeader>
          {candidateDetailPopup && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Full Name</p>
                  <p className="font-medium">{getCandidateName(candidateDetailPopup)}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Mail className="h-3 w-3" /> Email</p>
                  <p className="font-medium">{candidateDetailPopup.data?.email || 'N/A'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</p>
                  <p className="font-medium font-mono">{getPhone(candidateDetailPopup) || 'N/A'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Location</p>
                  <p className="font-medium">{candidateDetailPopup.data?.location || candidateDetailPopup.data?.city || 'N/A'}</p>
                </div>
              </div>

              {/* Additional Details */}
              <div className="border-t border-border pt-4">
                <h4 className="text-sm font-semibold mb-3">Additional Information</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <span>{candidateDetailPopup.data?.status || candidateDetailPopup.data?.submission_status || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Experience:</span>
                    <span>{candidateDetailPopup.data?.total_experience || candidateDetailPopup.data?.experience_years || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Source:</span>
                    <span>{candidateDetailPopup.data?.source || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Work Authorization:</span>
                    <span>{candidateDetailPopup.data?.work_authorization || candidateDetailPopup.data?.visa_status || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expected Rate:</span>
                    <span>{candidateDetailPopup.data?.expected_pay || candidateDetailPopup.data?.pay_rate || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Notice Period:</span>
                    <span>{candidateDetailPopup.data?.notice_period || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Skills */}
              {(candidateDetailPopup.data?.skills || candidateDetailPopup.data?.key_skills) && (
                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-semibold mb-2">Skills</h4>
                  <p className="text-sm text-muted-foreground">{candidateDetailPopup.data?.skills || candidateDetailPopup.data?.key_skills}</p>
                </div>
              )}

              {/* Raw Data Section (Collapsible) */}
              <div className="border-t border-border pt-4">
                <details className="group">
                  <summary className="text-sm font-semibold cursor-pointer flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    All CEIPAL Fields
                    <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg max-h-60 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {JSON.stringify(candidateDetailPopup.data || {}, null, 2)}
                    </pre>
                  </div>
                </details>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Job Detail Popup */}
      <Dialog open={!!jobDetailPopup} onOpenChange={() => setJobDetailPopup(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-purple-500" />
              Job Details
            </DialogTitle>
            <DialogDescription>
              Full job information from CEIPAL
            </DialogDescription>
          </DialogHeader>
          {jobDetailPopup && (
            <div className="space-y-4">
              {/* Job Header */}
              <div className="p-4 bg-purple-500/10 dark:bg-purple-500/20 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">{getJobTitle(jobDetailPopup)}</h3>
                    <p className="text-muted-foreground">{jobDetailPopup.data?.client}</p>
                  </div>
                  <Badge variant="outline" className="font-mono">
                    ID: {getJobId(jobDetailPopup)}
                  </Badge>
                </div>
              </div>

              {/* Job Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Location</p>
                  <p className="font-medium">{jobDetailPopup.data?.job_location || jobDetailPopup.data?.location || 'N/A'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Employment Type</p>
                  <p className="font-medium">{jobDetailPopup.data?.employment_type || jobDetailPopup.data?.job_type || 'N/A'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Pay Rate</p>
                  <p className="font-medium">{jobDetailPopup.data?.pay_rate || jobDetailPopup.data?.bill_rate || 'N/A'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Duration</p>
                  <p className="font-medium">{jobDetailPopup.data?.duration || 'N/A'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Positions</p>
                  <p className="font-medium">{jobDetailPopup.data?.positions || jobDetailPopup.data?.openings || 'N/A'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Status</p>
                  <p className="font-medium">{jobDetailPopup.data?.job_status || 'N/A'}</p>
                </div>
              </div>

              {/* Job Description */}
              <div className="border-t border-border pt-4">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Job Description
                </h4>
                <div className="p-4 bg-muted/30 rounded-lg max-h-60 overflow-y-auto">
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    {jobDetailPopup.data?.job_description || jobDetailPopup.data?.job_requirements || jobDetailPopup.data?.description || (
                      <p className="text-muted-foreground italic">No job description available</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Requirements */}
              {jobDetailPopup.data?.required_skills && (
                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-semibold mb-2">Required Skills</h4>
                  <p className="text-sm text-muted-foreground">{jobDetailPopup.data?.required_skills}</p>
                </div>
              )}

              {/* Raw Data */}
              <div className="border-t border-border pt-4">
                <details className="group">
                  <summary className="text-sm font-semibold cursor-pointer flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    All Job Fields
                    <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg max-h-60 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {JSON.stringify(jobDetailPopup.data || {}, null, 2)}
                    </pre>
                  </div>
                </details>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Calls List Popup */}
      <Dialog open={!!callsListPopup} onOpenChange={() => { setCallsListPopup(null); setCallsList([]); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneCall className="h-5 w-5 text-green-500" />
              Calls for {callsListPopup ? getCandidateName(callsListPopup) : ''}
            </DialogTitle>
            <DialogDescription>
              {callsListPopup && (
                <span className="font-mono">{getPhone(callsListPopup)}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {loadingCalls ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : callsList.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <PhoneCall className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No calls found for this phone number</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{callsList.length} call(s) found</p>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Date/Time</th>
                        <th className="text-left p-3 font-medium">Recruiter</th>
                        <th className="text-left p-3 font-medium">Duration</th>
                        <th className="text-left p-3 font-medium">Type</th>
                        <th className="text-left p-3 font-medium">Score</th>
                        <th className="text-left p-3 font-medium">Alert</th>
                        <th className="text-left p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {callsList.map((call) => (
                        <tr key={call.id} className="hover:bg-muted/30">
                          <td className="p-3">
                            <div>
                              <p>{new Date(call.call_date).toLocaleDateString()}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(call.call_date).toLocaleTimeString()}
                              </p>
                            </div>
                          </td>
                          <td className="p-3">{call.user_name || 'Unknown'}</td>
                          <td className="p-3">
                            {call.duration ? `${Math.round(call.duration / 60)}m ${Math.round(call.duration % 60)}s` : 'N/A'}
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs">
                              {call.detected_call_type || call.call_type || 'N/A'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            {call.average_score ? (
                              <span className={`font-medium ${call.average_score >= 7 ? 'text-green-500' : call.average_score >= 5 ? 'text-yellow-500' : 'text-red-500'}`}>
                                {call.average_score.toFixed(1)}/10
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge className={
                              call.alert_level === 'critical' ? 'bg-red-500' :
                              call.alert_level === 'high_alert' ? 'bg-orange-500' :
                              call.alert_level === 'needs_review' ? 'bg-yellow-500' :
                              'bg-green-500'
                            }>
                              {call.alert_level || 'good'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <Link
                              to={isAdmin ? `/admin/${window.location.pathname.split('/')[2]}/calls/${call.id}` : `/user/calls/${call.id}`}
                              target="_blank"
                              className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                            >
                              <Eye className="h-4 w-4" />
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Manual Submission Dialog */}
      <AddManualSubmissionDialog
        open={showAddManual}
        onClose={() => setShowAddManual(false)}
        tenantId={tenantId}
        onSuccess={() => {
          setShowAddManual(false);
          loadSubmissions();
        }}
      />
    </div>
  );
}

// ==================== API CONFIG TAB ====================
function APIConfigTab({ tenantId, config, onConfigSaved }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    api_key: '',
    jobs_endpoint: '',
    submissions_endpoint: '',
    use_sandbox: false,
    sync_interval_hours: 6,
    auto_sync_enabled: true,
    retention_days: 180
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (config?.config) {
      setFormData({
        email: config.config.email || '',
        password: '', // Don't pre-fill password
        api_key: '', // Don't pre-fill API key
        jobs_endpoint: config.config.jobs_endpoint || '',
        submissions_endpoint: config.config.submissions_endpoint || '',
        use_sandbox: config.config.use_sandbox || false,
        sync_interval_hours: config.config.sync_interval_hours || 6,
        auto_sync_enabled: config.config.auto_sync_enabled !== false,
        retention_days: config.config.retention_days || 180
      });
    }
  }, [config]);

  const handleSave = async () => {
    if (!formData.email || (!config?.configured && (!formData.password || !formData.api_key))) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      await saveSubmissionsConfig(tenantId, formData);
      toast.success('Configuration saved successfully!');
      onConfigSaved();
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const { data } = await testSubmissionsConnection(tenantId);
      setTestResult(data);
      if (data.success) {
        toast.success('Connection test successful!');
      } else {
        toast.error('Connection test failed');
      }
    } catch (error) {
      console.error('Test failed:', error);
      setTestResult({ success: false, error: error.response?.data?.detail || error.message });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Configuration Form */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-400" />
            CEIPAL API Configuration
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Configure your CEIPAL ATS credentials to sync jobs and submissions data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground/70">Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="your-email@company.com"
              className="bg-muted border-input"
              data-testid="ceipal-email"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground/70">
              Password {config?.configured && <span className="text-muted-foreground">(leave blank to keep current)</span>}
            </Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder={config?.configured ? '••••••••' : 'Enter password'}
              className="bg-muted border-input"
              data-testid="ceipal-password"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground/70">
              API Key {config?.configured && <span className="text-muted-foreground">(leave blank to keep current)</span>}
            </Label>
            <Input
              type="password"
              value={formData.api_key}
              onChange={(e) => setFormData({...formData, api_key: e.target.value})}
              placeholder={config?.configured ? '••••••••' : 'Enter API key'}
              className="bg-muted border-input"
              data-testid="ceipal-apikey"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground/70">Custom Jobs API Endpoint</Label>
            <Input
              type="text"
              value={formData.jobs_endpoint}
              onChange={(e) => setFormData({...formData, jobs_endpoint: e.target.value})}
              placeholder="/v1/getJobs/ or full URL"
              className="bg-muted border-input"
              data-testid="jobs-endpoint-input"
            />
            <p className="text-muted-foreground text-xs">Leave blank to use standard CEIPAL Jobs API (/v1/getJobs/)</p>
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground/70">Custom Submissions API Endpoint</Label>
            <Input
              type="text"
              value={formData.submissions_endpoint}
              onChange={(e) => setFormData({...formData, submissions_endpoint: e.target.value})}
              placeholder="/v1/getSubmissions/ or full URL"
              className="bg-muted border-input"
              data-testid="submissions-endpoint-input"
            />
            <p className="text-muted-foreground text-xs">Leave blank to use standard CEIPAL Submissions API (/v1/getSubmissions/)</p>
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-muted-foreground/70">Use Sandbox Environment</Label>
              <p className="text-muted-foreground text-xs">For testing purposes only</p>
            </div>
            <Switch
              checked={formData.use_sandbox}
              onCheckedChange={(v) => setFormData({...formData, use_sandbox: v})}
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-muted-foreground/70">Auto Sync</Label>
              <p className="text-muted-foreground text-xs">Automatically sync data periodically</p>
            </div>
            <Switch
              checked={formData.auto_sync_enabled}
              onCheckedChange={(v) => setFormData({...formData, auto_sync_enabled: v})}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground/70">Data Retention (days)</Label>
            <Select 
              value={formData.retention_days.toString()} 
              onValueChange={(v) => setFormData({...formData, retention_days: parseInt(v)})}
            >
              <SelectTrigger className="bg-muted border-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">180 days (6 months)</SelectItem>
                <SelectItem value="365">365 days (1 year)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="save-config-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Configuration
            </Button>
            {config?.configured && (
              <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="test-config-btn">
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                Test
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documentation & Test Results */}
      <div className="space-y-6">
        {/* Test Results */}
        {testResult && (
          <Card className={`border ${testResult.success ? 'border-green-500/50 bg-green-500/10' : 'border-red-500/50 bg-red-500/10'}`}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                {testResult.success ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                Connection Test {testResult.success ? 'Successful' : 'Failed'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {testResult.authentication && (
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span className="text-muted-foreground/70">Authentication: {testResult.authentication}</span>
                  </div>
                  
                  <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                    <h4 className="text-foreground font-medium text-xs uppercase tracking-wide">Jobs API</h4>
                    <div className="flex items-center gap-2">
                      {testResult.jobs_api === 'OK' ? (
                        <CheckCircle className="h-4 w-4 text-green-400" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-400" />
                      )}
                      <span className={testResult.jobs_api === 'OK' ? 'text-green-400' : 'text-red-400'}>
                        {testResult.jobs_api}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      Endpoint: <code className="text-blue-400">{testResult.jobs_endpoint_used || '/v1/getJobs/'}</code>
                    </p>
                    {testResult.jobs_api === 'OK' && (
                      <p className="text-muted-foreground text-xs">Found {testResult.jobs_count} jobs</p>
                    )}
                  </div>
                  
                  <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                    <h4 className="text-foreground font-medium text-xs uppercase tracking-wide">Submissions API</h4>
                    <div className="flex items-center gap-2">
                      {testResult.submissions_api === 'OK' ? (
                        <CheckCircle className="h-4 w-4 text-green-400" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-400" />
                      )}
                      <span className={testResult.submissions_api === 'OK' ? 'text-green-400' : 'text-red-400'}>
                        {testResult.submissions_api}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      Endpoint: <code className="text-blue-400">{testResult.submissions_endpoint_used || '/v1/getSubmissions/'}</code>
                    </p>
                    {testResult.submissions_api === 'OK' && (
                      <p className="text-muted-foreground text-xs">Found {testResult.submissions_count} submissions</p>
                    )}
                  </div>
                </div>
              )}
              {testResult.error && (
                <p className="text-red-300">{testResult.error}</p>
              )}
              {testResult.details && (
                <p className="text-red-400 text-sm mt-2">{testResult.details}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Documentation */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-400" />
              CEIPAL API Documentation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <h4 className="text-foreground font-medium mb-2">Authentication</h4>
              <p className="text-muted-foreground">
                CEIPAL uses Bearer Token authentication. Your credentials are used to generate
                a token that&apos;s valid for 24 hours.
              </p>
            </div>
            
            <div>
              <h4 className="text-foreground font-medium mb-2">Default API Endpoints</h4>
              <ul className="text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400">Jobs:</span>
                  <code className="text-xs bg-slate-900 px-2 py-1 rounded">/v1/getJobs/</code>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400">Submissions:</span>
                  <code className="text-xs bg-slate-900 px-2 py-1 rounded">/v1/getSubmissions/</code>
                </li>
              </ul>
              <p className="text-muted-foreground text-xs mt-2">
                If your CEIPAL instance uses different endpoints, enter them in the custom endpoint fields above.
              </p>
            </div>

            <div>
              <h4 className="text-foreground font-medium mb-2">Rate Limits</h4>
              <p className="text-muted-foreground">
                CEIPAL API has rate limits based on your API key. Data is fetched in pages
                of 100 records.
              </p>
            </div>

            <div className="pt-2">
              <a
                href="https://developer.ceipal.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
              >
                View Full Documentation <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ==================== HELPER COMPONENTS ====================

function MetricCard({ title, value, suffix, subtitle, icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    red: 'bg-red-500/20 text-red-400',
    purple: 'bg-purple-500/20 text-purple-400',
    orange: 'bg-orange-500/20 text-orange-400'
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            {icon}
          </div>
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold text-foreground">
            {value}{suffix && <span className="text-lg text-muted-foreground">{suffix}</span>}
          </p>
          <p className="text-muted-foreground text-sm">{title}</p>
          {subtitle && <p className="text-muted-foreground text-xs mt-1">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function FitScoreBadge({ score }) {
  const getColor = () => {
    if (score >= 75) return 'bg-green-500/20 text-green-400 border-green-400/30';
    if (score >= 60) return 'bg-yellow-500/20 text-yellow-400 border-yellow-400/30';
    if (score >= 40) return 'bg-orange-500/20 text-orange-400 border-orange-400/30';
    return 'bg-red-500/20 text-red-400 border-red-400/30';
  };

  const getLabel = () => {
    if (score >= 90) return 'Strong Fit';
    if (score >= 75) return 'Good Fit';
    if (score >= 60) return 'Moderate';
    if (score >= 40) return 'Weak Fit';
    return 'Not a Fit';
  };

  return (
    <Badge variant="outline" className={getColor()}>
      {score} - {getLabel()}
    </Badge>
  );
}

function ScoreDistributionChart({ data }) {
  const labels = ['0-39', '40-59', '60-74', '75-89', '90-100'];
  const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981'];
  
  const chartData = labels.map((label, idx) => {
    const bucket = data.find(d => d._id === idx * 20 || d._id === (idx === 4 ? 90 : idx * 20));
    return bucket?.count || 0;
  });

  const maxValue = Math.max(...chartData, 1);

  return (
    <div className="space-y-3">
      {labels.map((label, idx) => (
        <div key={label} className="flex items-center gap-3">
          <span className="text-muted-foreground text-sm w-16">{label}</span>
          <div className="flex-1 bg-slate-700 rounded-full h-6 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${(chartData[idx] / maxValue) * 100}%`,
                backgroundColor: colors[idx]
              }}
            />
          </div>
          <span className="text-foreground text-sm w-8">{chartData[idx]}</span>
        </div>
      ))}
    </div>
  );
}

function TrendChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground">
        No trend data available
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.count), 1);
  const chartHeight = 180;

  return (
    <div className="h-48">
      <div className="flex items-end justify-between h-full gap-1">
        {data.map((item, idx) => {
          const height = (item.count / maxValue) * (chartHeight - 30);
          const date = new Date(item._id);
          const dayLabel = date.getDate();
          return (
            <div key={item._id || idx} className="flex-1 flex flex-col items-center group">
              <div 
                className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-400 relative"
                style={{ height: `${Math.max(height, 4)}px` }}
              >
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 px-2 py-1 rounded text-xs text-foreground whitespace-nowrap">
                  {item.count} submissions
                </div>
              </div>
              {idx % Math.ceil(data.length / 7) === 0 && (
                <span className="text-xs text-muted-foreground mt-1">{dayLabel}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RecommendationBreakdown({ data, total }) {
  const recommendations = [
    { label: 'Strong Fit', range: '90-100', color: '#10b981', bgColor: 'bg-emerald-500/20' },
    { label: 'Good Fit', range: '75-89', color: '#22c55e', bgColor: 'bg-green-500/20' },
    { label: 'Moderate Fit', range: '60-74', color: '#eab308', bgColor: 'bg-yellow-500/20' },
    { label: 'Weak Fit', range: '40-59', color: '#f97316', bgColor: 'bg-orange-500/20' },
    { label: 'Not a Fit', range: '0-39', color: '#ef4444', bgColor: 'bg-red-500/20' }
  ];

  // Map score distribution to recommendations
  const getCounts = () => {
    const counts = [0, 0, 0, 0, 0]; // Strong, Good, Moderate, Weak, Not
    data.forEach(bucket => {
      if (bucket._id >= 90) counts[0] += bucket.count;
      else if (bucket._id >= 75) counts[1] += bucket.count;
      else if (bucket._id >= 60) counts[2] += bucket.count;
      else if (bucket._id >= 40) counts[3] += bucket.count;
      else counts[4] += bucket.count;
    });
    return counts;
  };

  const counts = getCounts();
  const totalCount = counts.reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-3">
      {recommendations.map((rec, idx) => {
        const count = counts[idx];
        const percentage = Math.round((count / totalCount) * 100);
        return (
          <div key={rec.label} className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: rec.color }} />
            <span className="text-muted-foreground/70 text-sm flex-1">{rec.label}</span>
            <div className="w-32 bg-slate-700 rounded-full h-2 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${percentage}%`, backgroundColor: rec.color }}
              />
            </div>
            <span className="text-muted-foreground text-sm w-16 text-right">{count} ({percentage}%)</span>
          </div>
        );
      })}
      <div className="pt-2 border-t border-border">
        <p className="text-muted-foreground text-xs text-center">
          Total analyzed: {totalCount} submissions
        </p>
      </div>
    </div>
  );
}

function CorrelationChart({ data }) {
  const ranges = [
    { key: 'excellent_calls', label: 'Excellent (80-100)', color: '#10b981' },
    { key: 'good_calls', label: 'Good (60-79)', color: '#22c55e' },
    { key: 'average_calls', label: 'Average (40-59)', color: '#eab308' },
    { key: 'poor_calls', label: 'Poor (0-39)', color: '#ef4444' }
  ];

  return (
    <div className="space-y-3">
      {ranges.map((range) => {
        const item = data[range.key] || { count: 0, avg_fit_score: 0 };
        return (
          <div key={range.key} className="flex items-center gap-3">
            <span className="text-muted-foreground text-sm w-32">{range.label}</span>
            <div className="flex-1 bg-slate-700 rounded-full h-6 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                style={{
                  width: `${item.avg_fit_score}%`,
                  backgroundColor: range.color
                }}
              >
                {item.avg_fit_score > 20 && (
                  <span className="text-foreground text-xs font-medium">{item.avg_fit_score}</span>
                )}
              </div>
            </div>
            <span className="text-muted-foreground text-xs w-12">({item.count})</span>
          </div>
        );
      })}
    </div>
  );
}

// ==================== MATCHED CALLS SECTION ====================
function MatchedCallsSection({ calls }) {
  const [playingCallId, setPlayingCallId] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const audioRef = useRef(null);

  const displayCalls = showAll ? calls : calls.slice(0, 5);

  const handlePlayCall = async (call) => {
    // If same call is playing, pause it
    if (playingCallId === call.id && audioRef.current) {
      audioRef.current.pause();
      setPlayingCallId(null);
      return;
    }

    // Load and play new call
    setLoadingAudio(true);
    try {
      const url = await getCallAudioUrl(call.id);
      setAudioUrl(url);
      setPlayingCallId(call.id);
      
      // Wait for audio element to be ready
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(err => {
            console.error('Audio play error:', err);
            toast.error('Unable to play audio');
          });
        }
      }, 100);
    } catch (error) {
      console.error('Error loading audio:', error);
      toast.error(error.message || 'Unable to load audio');
      setPlayingCallId(null);
    } finally {
      setLoadingAudio(false);
    }
  };

  const handleAudioEnded = () => {
    setPlayingCallId(null);
  };

  const openCallDetail = (callId) => {
    window.open(`/calls/${callId}`, '_blank');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getScoreColor = (score) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 8) return 'text-green-500';
    if (score >= 6) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div>
      <h4 className="text-foreground font-medium mb-3 flex items-center gap-2">
        <Phone className="h-4 w-4 text-green-500" />
        Matched Calls ({calls.length})
      </h4>
      
      {/* Hidden audio element */}
      <audio 
        ref={audioRef} 
        src={audioUrl} 
        onEnded={handleAudioEnded}
        className="hidden"
      />
      
      <div className="space-y-2">
        {displayCalls.map((call, idx) => (
          <div 
            key={call.id || idx} 
            className="p-3 bg-muted/50 rounded-lg flex items-center justify-between gap-3 hover:bg-muted/70 transition-colors"
          >
            {/* Play Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 flex-shrink-0"
              onClick={() => handlePlayCall(call)}
              disabled={loadingAudio && playingCallId !== call.id}
              data-testid={`play-call-${call.id}`}
            >
              {loadingAudio && playingCallId === call.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : playingCallId === call.id ? (
                <Pause className="h-4 w-4 text-primary" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>

            {/* Call Info */}
            <div className="flex-1 min-w-0">
              <p className="text-foreground text-sm font-medium truncate">
                {call.filename || call.id}
              </p>
              <p className="text-muted-foreground text-xs">
                {formatDate(call.created_at)}
                {call.call_type && (
                  <span className="ml-2 capitalize">{call.call_type.replace(/_/g, ' ')}</span>
                )}
              </p>
            </div>

            {/* Score */}
            {call.average_score && (
              <div className={`text-sm font-semibold ${getScoreColor(call.average_score)}`}>
                {Math.round(call.average_score)}/10
              </div>
            )}

            {/* Open in New Tab Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 flex-shrink-0 hover:text-primary"
              onClick={() => openCallDetail(call.id)}
              title="Open call details in new tab"
              data-testid={`open-call-${call.id}`}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Show More/Less Button */}
      {calls.length > 5 && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-2 text-muted-foreground hover:text-foreground"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? `Show Less` : `Show All ${calls.length} Calls`}
        </Button>
      )}

      {/* Now Playing Indicator */}
      {playingCallId && (
        <div className="mt-3 p-2 bg-primary/10 rounded-lg flex items-center gap-2 text-sm">
          <Volume2 className="h-4 w-4 text-primary animate-pulse" />
          <span className="text-foreground">
            Now playing: {calls.find(c => c.id === playingCallId)?.filename || playingCallId}
          </span>
        </div>
      )}
    </div>
  );
}

function SubmissionDetailDialog({ open, onClose, data, loading, onAnalyze }) {
  if (!data) return null;

  const { submission, job_data, matched_calls, analysis: savedAnalysis } = data;
  const analysis = submission?.analysis || savedAnalysis?.analysis;
  
  // Helper to get candidate name from various possible fields
  const getCandidateName = () => {
    const d = submission?.data || {};
    return d.applicant_full_name || d.applicant_name || d.candidate_name || d.full_name || d.name || 'Submission Details';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-400" />
            {getCandidateName()}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <ScrollArea className="max-h-[70vh]">
            <div className="space-y-6 pr-4">
              {/* Candidate Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="text-foreground">{submission?.data?.email || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="text-foreground">{submission?.data?.phone || submission?.data?.mobile || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Job Title</Label>
                  <p className="text-foreground">{submission?.data?.job_title || job_data?.job_title || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Client</Label>
                  <p className="text-foreground">{submission?.data?.client || job_data?.client || 'N/A'}</p>
                </div>
              </div>

              {/* AI Analysis */}
              {analysis ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                      <Brain className="h-5 w-5 text-purple-400" />
                      AI Analysis
                    </h3>
                    <FitScoreBadge score={analysis.overall_fit_score} />
                  </div>

                  <Card className="bg-muted/50 border-border">
                    <CardContent className="p-4">
                      <p className="text-muted-foreground/70">{analysis.recommendation_summary}</p>
                    </CardContent>
                  </Card>

                  {/* Resume vs Job Match */}
                  {analysis.resume_vs_job_analysis && (
                    <div>
                      <h4 className="text-foreground font-medium mb-2">Resume vs Job Match</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-green-400">Matched Skills</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(analysis.resume_vs_job_analysis.skills_match?.matched_skills || []).map((skill, i) => (
                              <Badge key={i} variant="outline" className="text-green-400 border-green-400/30">{skill}</Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="text-red-400">Missing Skills</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(analysis.resume_vs_job_analysis.skills_match?.missing_required_skills || []).map((skill, i) => (
                              <Badge key={i} variant="outline" className="text-red-400 border-red-400/30">{skill}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Red Flags */}
                  {analysis.red_flags && (analysis.red_flags.from_resume?.length > 0 || analysis.red_flags.from_calls?.length > 0) && (
                    <div>
                      <h4 className="text-red-400 font-medium mb-2">Red Flags</h4>
                      <ul className="list-disc list-inside text-muted-foreground/70 space-y-1">
                        {[...(analysis.red_flags.from_resume || []), ...(analysis.red_flags.from_calls || [])].map((flag, i) => (
                          <li key={i}>{flag}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recruiter Feedback */}
                  {analysis.recruiter_feedback && (
                    <div>
                      <h4 className="text-blue-400 font-medium mb-2">Recruiter Feedback</h4>
                      {analysis.recruiter_feedback.areas_for_improvement?.length > 0 && (
                        <div className="mb-2">
                          <Label className="text-muted-foreground">Areas for Improvement</Label>
                          <ul className="list-disc list-inside text-muted-foreground/70 space-y-1">
                            {analysis.recruiter_feedback.areas_for_improvement.map((area, i) => (
                              <li key={i}>{area}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {analysis.recruiter_feedback.suggested_questions_for_future?.length > 0 && (
                        <div>
                          <Label className="text-muted-foreground">Suggested Questions for Future</Label>
                          <ul className="list-disc list-inside text-muted-foreground/70 space-y-1">
                            {analysis.recruiter_feedback.suggested_questions_for_future.map((q, i) => (
                              <li key={i}>{q}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Card className="bg-muted/50 border-border">
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <Brain className="h-12 w-12 text-muted-foreground mb-3" />
                    <p className="text-muted-foreground mb-4">This submission has not been analyzed yet</p>
                    <Button onClick={() => onAnalyze(submission.submission_id)}>
                      <Brain className="h-4 w-4 mr-2" />
                      Run AI Analysis
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Matched Calls */}
              {matched_calls && matched_calls.length > 0 && (
                <MatchedCallsSection calls={matched_calls} />
              )}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddManualSubmissionDialog({ open, onClose, tenantId, onSuccess }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    phone_number: '',
    candidate_name: '',
    candidate_email: '',
    job_title: '',
    job_description: '',
    client_name: '',
    resume_text: '',
    notes: ''
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoAnalyze, setAutoAnalyze] = useState(true);
  const [createdSubmission, setCreatedSubmission] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        phone_number: '',
        candidate_name: '',
        candidate_email: '',
        job_title: '',
        job_description: '',
        client_name: '',
        resume_text: '',
        notes: ''
      });
      setResumeFile(null);
      setCreatedSubmission(null);
    }
  }, [open]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx|txt)$/i)) {
        toast.error('Please upload a PDF, Word document, or text file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setResumeFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!formData.phone_number || !formData.candidate_name || !formData.job_title) {
      toast.error('Please fill in required fields (Phone, Name, Job Title)');
      return;
    }

    try {
      setSaving(true);
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('tenant_id', tenantId);
      submitData.append('phone_number', formData.phone_number);
      submitData.append('candidate_name', formData.candidate_name);
      submitData.append('job_title', formData.job_title);
      submitData.append('job_description', formData.job_description || '');
      submitData.append('candidate_email', formData.candidate_email || '');
      submitData.append('client_name', formData.client_name || '');
      submitData.append('resume_text', formData.resume_text || '');
      submitData.append('notes', formData.notes || '');
      submitData.append('recruiter_id', user?.id || '');
      submitData.append('recruiter_name', user?.name || '');
      submitData.append('auto_analyze', autoAnalyze.toString());
      
      if (resumeFile) {
        submitData.append('resume_file', resumeFile);
      }
      
      const { data } = await createManualSubmissionWithFile(tenantId, submitData);
      
      setCreatedSubmission(data);
      
      if (data.auto_analyzed && data.analysis) {
        toast.success('Submission created and AI analysis completed!');
      } else if (data.matched_calls_count > 0) {
        toast.success(`Submission created! Found ${data.matched_calls_count} matching call(s).`);
      } else {
        toast.success('Submission created successfully!');
      }
      
      onSuccess();
      
    } catch (error) {
      console.error('Failed to create submission:', error);
      toast.error(error.response?.data?.detail || 'Failed to create submission');
    } finally {
      setSaving(false);
    }
  };

  const handleAnalyzeNow = async () => {
    if (!createdSubmission?.submission_id) return;
    
    try {
      setAnalyzing(true);
      const { data } = await analyzeSubmission(tenantId, createdSubmission.submission_id);
      toast.success('AI Analysis completed!');
      setCreatedSubmission(prev => ({
        ...prev,
        auto_analyzed: true,
        analysis: data.analysis
      }));
      onSuccess();
    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error('Analysis failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-400" />
            Add Manual Submission
          </DialogTitle>
          <DialogDescription>
            Create a submission entry manually. Upload a resume and the AI will analyze candidate fit.
          </DialogDescription>
        </DialogHeader>

        {createdSubmission ? (
          // Success state
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckCircle className="h-8 w-8 text-green-400" />
              <div>
                <h4 className="font-semibold text-green-400">Submission Created!</h4>
                <p className="text-sm text-muted-foreground/70">
                  {createdSubmission.matched_calls_count > 0 
                    ? `Found ${createdSubmission.matched_calls_count} matching call(s)`
                    : 'No matching calls found yet'}
                </p>
              </div>
            </div>
            
            {createdSubmission.auto_analyzed && createdSubmission.analysis && (
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <h4 className="font-semibold text-blue-400 flex items-center gap-2 mb-2">
                  <Brain className="h-5 w-5" />
                  AI Analysis Complete
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Fit Score:</span>
                    <span className="ml-2 font-bold text-lg">
                      {createdSubmission.analysis.overall_fit_score || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Recommendation:</span>
                    <Badge className="ml-2" variant={
                      createdSubmission.analysis.overall_recommendation?.includes('STRONG') ? 'success' :
                      createdSubmission.analysis.overall_recommendation?.includes('GOOD') ? 'success' :
                      createdSubmission.analysis.overall_recommendation?.includes('MODERATE') ? 'warning' : 'destructive'
                    }>
                      {createdSubmission.analysis.overall_recommendation?.replace(/_/g, ' ') || 'N/A'}
                    </Badge>
                  </div>
                </div>
                {createdSubmission.analysis.recommendation_summary && (
                  <p className="mt-2 text-sm text-muted-foreground/70">{createdSubmission.analysis.recommendation_summary}</p>
                )}
              </div>
            )}
            
            {!createdSubmission.auto_analyzed && (
              <div className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-lg">
                <Brain className="h-6 w-6 text-purple-400" />
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground/70">Run AI analysis to get candidate fit score and recommendations</p>
                </div>
                <Button onClick={handleAnalyzeNow} disabled={analyzing}>
                  {analyzing ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyzing...</>
                  ) : (
                    <><Brain className="h-4 w-4 mr-2" />Analyze Now</>
                  )}
                </Button>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button onClick={() => setCreatedSubmission(null)}>
                <Plus className="h-4 w-4 mr-2" />Add Another
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // Form state
          <>
            <div className="space-y-4 py-2">
              {/* Candidate Info Section */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Candidate Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone Number <span className="text-red-400">*</span></Label>
                    <Input
                      value={formData.phone_number}
                      onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                      placeholder="555-123-4567"
                      data-testid="manual-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Candidate Name <span className="text-red-400">*</span></Label>
                    <Input
                      value={formData.candidate_name}
                      onChange={(e) => setFormData({...formData, candidate_name: e.target.value})}
                      placeholder="John Doe"
                      data-testid="manual-name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.candidate_email}
                    onChange={(e) => setFormData({...formData, candidate_email: e.target.value})}
                    placeholder="john@example.com"
                    data-testid="manual-email"
                  />
                </div>
              </div>

              {/* Job Info Section */}
              <div className="space-y-3 pt-2 border-t border-border">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Job Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Job Title <span className="text-red-400">*</span></Label>
                    <Input
                      value={formData.job_title}
                      onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                      placeholder="Software Engineer"
                      data-testid="manual-job-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                      placeholder="Client Company Name"
                      data-testid="manual-client"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Job Description</Label>
                  <Textarea
                    value={formData.job_description}
                    onChange={(e) => setFormData({...formData, job_description: e.target.value})}
                    placeholder="Paste the full job description here for better AI analysis..."
                    rows={5}
                    data-testid="manual-jd"
                  />
                </div>
              </div>

              {/* Resume Section */}
              <div className="space-y-3 pt-2 border-t border-border">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Resume
                </h4>
                
                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Upload Resume (PDF, Word, or Text)</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={handleFileChange}
                      className="flex-1"
                      data-testid="manual-resume-file"
                    />
                    {resumeFile && (
                      <Badge variant="secondary" className="whitespace-nowrap">
                        <FileText className="h-3 w-3 mr-1" />
                        {resumeFile.name}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Max 10MB. PDF and Word documents will be parsed automatically.</p>
                </div>
                
                {/* Or paste text */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">or paste resume text</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Textarea
                    value={formData.resume_text}
                    onChange={(e) => setFormData({...formData, resume_text: e.target.value})}
                    placeholder="Paste the resume content here..."
                    rows={5}
                    data-testid="manual-resume-text"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2 pt-2 border-t border-border">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any additional notes about this submission..."
                  rows={2}
                  data-testid="manual-notes"
                />
              </div>

              {/* Auto-Analyze Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Brain className="h-5 w-5 text-purple-400" />
                  <div>
                    <p className="font-medium text-sm">Auto-Analyze with AI</p>
                    <p className="text-xs text-muted-foreground">Automatically run AI analysis after saving</p>
                  </div>
                </div>
                <Switch
                  checked={autoAnalyze}
                  onCheckedChange={setAutoAnalyze}
                  data-testid="auto-analyze-toggle"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={saving} data-testid="create-submission-btn">
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
                ) : (
                  <><Plus className="h-4 w-4 mr-2" />Create Submission</>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
